import React, { useState, useMemo, useCallback } from 'react';
import { Calculator } from '../../types';
import { Icon } from '../Icon';
import { CalculatorShell } from './CalculatorShell';
import { exportToPDF, shareToWhatsApp } from '../../utils/exportUtils';
import { maskNumber, maskInteger, parseBrazilianNumber } from '../../utils/formatUtils';
import { FormField, ResultCard, EducationalBlock } from './finance/shared';

// Money and Finance
import IRPFCalculator from './finance/IRPFCalculator';
import InvestmentCalculator from './finance/InvestmentCalculator';
import PercentageCalculator from './finance/PercentageCalculator';
import InterestCalculator from './finance/InterestCalculator';
import FuelCalculator from './finance/FuelCalculator';
import RuleOfThreeCalculator from './finance/RuleOfThreeCalculator';
import EquivalentRateCalculator from './finance/EquivalentRateCalculator';
import FuelConsumptionCalculator from './finance/FuelConsumptionCalculator';
import CurrencyConverter from './finance/CurrencyConverter';
import CashbackCalculator from './finance/CashbackCalculator';
import PointsMilesCalculator from './finance/PointsMilesCalculator';
import FinancingCalculator from './finance/FinancingCalculator';
import FixedIncomeCalculator from './finance/FixedIncomeCalculator';
import SavingsYieldCalculator from './finance/SavingsYieldCalculator';
import EmergencyFundCalculator from './finance/EmergencyFundCalculator';
import InstallmentCalculator from './finance/InstallmentCalculator';

// Work
import FGTSCalculator from './work/FGTSCalculator';
import INSSCalculator from './work/INSSCalculator';
import DecimoTerceiroCalculator from './work/DecimoTerceiroCalculator';
import FeriasCalculator from './work/FeriasCalculator';
import RescisaoCalculator from './work/RescisaoCalculator';
import NetSalaryCalculator from './work/NetSalaryCalculator';
import EmployeeCostCalculator from './work/EmployeeCostCalculator';
import GrossSalaryCalculator from './work/GrossSalaryCalculator';
import ProportionalVacationCalculator from './work/ProportionalVacationCalculator';
import OvertimeCalculator from './work/OvertimeCalculator';
import UnemploymentInsuranceCalculator from './work/UnemploymentInsuranceCalculator';
import PJvsCLTCalculator from './work/PJvsCLTCalculator';

// Math
import ArithmeticMeanCalculator from './math/ArithmeticMeanCalculator';
import WeightedMeanCalculator from './math/WeightedMeanCalculator';
import UnitConverter from './math/UnitConverter';
import FractionCalculator from './math/FractionCalculator';
import PriceComparisonCalculator from './math/PriceComparisonCalculator';
import PercentageChangeCalculator from './math/PercentageChangeCalculator';
import CompoundRuleOfThreeCalculator from './math/CompoundRuleOfThreeCalculator';
import RatioProportionCalculator from './math/RatioProportionCalculator';

// Health
import CaloriesCalculator from './health/CaloriesCalculator';
import MealCaloriesCalculator from './health/MealCaloriesCalculator';
import MicronutrientsCalculator from './health/MicronutrientsCalculator';
import IdealWeightCalculator from './health/IdealWeightCalculator';
import BodyFatCalculator from './health/BodyFatCalculator';
import WeightLossCaloriesCalculator from './health/WeightLossCaloriesCalculator';
import MuscleGainCaloriesCalculator from './health/MuscleGainCaloriesCalculator';

// Date
import DateCalculator from './date/DateCalculator';
import ElapsedDaysCalculator from './date/ElapsedDaysCalculator';

// Utilities
import EnergyCalculator from './utilities/EnergyCalculator';
import VehicleFinancingCalculator from './utilities/VehicleFinancingCalculator';
import IPVACalculator from './utilities/IPVACalculator';
import WaterConsumptionCalculator from './utilities/WaterConsumptionCalculator';
import TransportCalculator from './utilities/TransportCalculator';

interface AllCalculatorsModuleProps {
  calculator: Calculator;
  onBack: () => void;
  isFavorite?: boolean;
  onToggleFavorite?: () => void;
}








// Componente Principal
export const AllCalculatorsModule: React.FC<AllCalculatorsModuleProps> = ({
  calculator,
  onBack,
  isFavorite,
  onToggleFavorite,
}) => {
  // Rolar para o topo ao entrar na calculadora
  React.useEffect(() => {
    // Forçar rolagem imediata e novamente após um pequeno delay
    window.scrollTo(0, 0);
  }, [calculator.slug]);

  const [hasScrolled, setHasScrolled] = React.useState(false);

  React.useEffect(() => {
    // Resetar flag quando mudar de calculadora
    setHasScrolled(false);
  }, [calculator.slug]);

  const [exportData, setExportData] = useState<{ title: string, results: { label: string, value: string }[] } | null>(null);

  const handleResultChange = React.useCallback((data: { label: string, value: string }[]) => {
    setExportData({ title: calculator.title, results: data });
  }, [calculator.title]);

  const handleExportPDF = () => {
    if (!exportData) return;
    exportToPDF({
      title: exportData.title,
      description: `Relatório gerado em ${new Date().toLocaleDateString('pt-BR')}`,
      results: exportData.results
    });
  };

  const handleShareWhatsApp = () => {
    if (!exportData) return;
    shareToWhatsApp(exportData.title, exportData.results);
  };

  const renderCalculator = () => {
    // Lista de calculadoras que já foram implementadas
    const implementedSlugs = [
      'porcentagem', 'juros-simples', 'juros-compostos', 'investimentos',
      'financiamento-price', 'financiamento-sac', 'imposto-de-renda',
      'taxa-equivalente', 'regra-de-tres-simples', 'alcool-ou-gasolina',
      'consumo-energia', 'moedas', 'pontos-milhas', 'renda-fixa', 'salario-liquido',
      'decimo-terceiro', 'ferias', 'rescisao', 'imc', 'tmb',
      'gasto-calorico', 'macronutrientes', 'agua-diaria', 'idade',
      'calculadora-datas', 'emprestimo', 'financiamento-imobiliario', 'orcamento-mensal',
      'meta-financeira', 'viagem', 'custo-streaming',
      'custo-transporte', 'media-aritmetica',
      'conversoes', 'media-ponderada', 'fracoes', 'custo-funcionario',
      'salario-bruto', 'ferias-proporcionais', 'fgts', 'inss', 'hora-extra',
      'seguro-desemprego', 'pj-x-clt', 'financiamento-veiculos',
      'ipva', 'rendimento-poupanca', 'calorias-refeicao',
      'objetivo-calorico', 'calorias-hipertrofia', 'reserva-emergencia', 'parcelamento', 'comparar-precos',
      'consumo-agua-residencial', 'regra-de-tres-composta', 'razao-proporcao'
    ];

    // Implementações específicas para calculadoras
    switch (calculator.slug) {
      case 'porcentagem':
        return <PercentageCalculator onResultChange={handleResultChange} />;
      case 'juros-simples':
        return <InterestCalculator type="simple" onResultChange={handleResultChange} />;
      case 'juros-compostos':
        return <InterestCalculator type="compound" onResultChange={handleResultChange} />;
      case 'investimentos':
        return <InvestmentCalculator onResultChange={handleResultChange} />;
      case 'financiamento-price':
        return <FinancingCalculator type="price" onResultChange={handleResultChange} />;
      case 'financiamento-sac':
        return <FinancingCalculator type="sac" onResultChange={handleResultChange} />;
      case 'imposto-de-renda':
        return <IRPFCalculator onResultChange={handleResultChange} />;
      case 'taxa-equivalente':
        return <EquivalentRateCalculator onResultChange={handleResultChange} />;
      case 'regra-de-tres-simples':
        return <RuleOfThreeCalculator onResultChange={handleResultChange} />;
      case 'alcool-ou-gasolina':
        return <FuelCalculator onResultChange={handleResultChange} />;
      case 'moedas':
        return <CurrencyConverter onResultChange={handleResultChange} />;
      case 'pontos-milhas':
        return <PointsMilesCalculator onResultChange={handleResultChange} />;
      case 'cashback':
        return <CashbackCalculator onResultChange={handleResultChange} />;
      case 'renda-fixa':
        return <FixedIncomeCalculator onResultChange={handleResultChange} />;
      case 'salario-liquido':
        return <NetSalaryCalculator onResultChange={handleResultChange} />;
      case 'decimo-terceiro':
        return <DecimoTerceiroCalculator onResultChange={handleResultChange} />;
      case 'ferias':
        return <FeriasCalculator onResultChange={handleResultChange} />;
      case 'rescisao':
        return <RescisaoCalculator onResultChange={handleResultChange} />;
      case 'fgts':
        return <FGTSCalculator onResultChange={handleResultChange} />;
      case 'inss':
        return <INSSCalculator onResultChange={handleResultChange} />;
      case 'media-aritmetica':
        return <ArithmeticMeanCalculator onResultChange={handleResultChange} />;
      case 'media-ponderada':
        return <WeightedMeanCalculator onResultChange={handleResultChange} />;
      case 'conversoes':
        return <UnitConverter onResultChange={handleResultChange} />;
      case 'calculadora-datas':
        return <DateCalculator onResultChange={handleResultChange} />;
      case 'fracoes':
        return <FractionCalculator onResultChange={handleResultChange} />;
      case 'consumo-energia':
        return <EnergyCalculator onResultChange={handleResultChange} />;
      case 'custo-funcionario':
        return <EmployeeCostCalculator onResultChange={handleResultChange} />;
      case 'salario-bruto':
        return <GrossSalaryCalculator onResultChange={handleResultChange} />;
      case 'ferias-proporcionais':
        return <ProportionalVacationCalculator onResultChange={handleResultChange} />;
      case 'hora-extra':
        return <OvertimeCalculator onResultChange={handleResultChange} />;
      case 'seguro-desemprego':
        return <UnemploymentInsuranceCalculator onResultChange={handleResultChange} />;
      case 'pj-x-clt':
        return <PJvsCLTCalculator onResultChange={handleResultChange} />;
      case 'financiamento-veiculos':
        return <VehicleFinancingCalculator onResultChange={handleResultChange} />;
      case 'ipva':
        return <IPVACalculator onResultChange={handleResultChange} />;
      case 'rendimento-poupanca':
        return <SavingsYieldCalculator onResultChange={handleResultChange} />;
      case 'calorias-refeicao':
        return <MealCaloriesCalculator onResultChange={handleResultChange} />;
      case 'objetivo-calorico':
        return <CaloriesCalculator onResultChange={handleResultChange} />;
      case 'calorias-hipertrofia':
        return <MuscleGainCaloriesCalculator onResultChange={handleResultChange} />;
      case 'reserva-emergencia':
        return <EmergencyFundCalculator onResultChange={handleResultChange} />;
      case 'parcelamento':
        return <InstallmentCalculator onResultChange={handleResultChange} />;
      case 'comparar-precos':
        return <PriceComparisonCalculator onResultChange={handleResultChange} />;
      case 'consumo-agua-residencial':
        return <WaterConsumptionCalculator onResultChange={handleResultChange} />;
      case 'custo-transporte':
        return <TransportCalculator onResultChange={handleResultChange} />;
      case 'regra-de-tres-composta':
        return <CompoundRuleOfThreeCalculator onResultChange={handleResultChange} />;
      case 'razao-proporcao':
        return <RatioProportionCalculator onResultChange={handleResultChange} />;
      case 'variacao-percentual':
        return <PercentageChangeCalculator onResultChange={handleResultChange} />;
      default:
        return (
          <div className="bg-theme-result p-12 rounded-3xl border-2 border-dashed border-theme-border text-center">
            <div className="w-20 h-20 bg-theme-input rounded-full flex items-center justify-center mx-auto mb-6">
              <Icon name="info" className="w-10 h-10 text-theme-muted" />
            </div>
            <h3 className="text-2xl font-black text-theme-text mb-4">Em Desenvolvimento</h3>
            <p className="text-theme-muted max-w-md mx-auto mb-8 font-medium">
              Esta calculadora ainda está sendo preparada para você. Em breve teremos novidades!
            </p>
            <button
              onClick={onBack}
              className="px-8 py-3 bg-theme-input hover:bg-theme-border text-theme-text rounded-xl font-bold transition-all"
            >
              Voltar para Explorar
            </button>
          </div>
        );
    }
  };

  return (
    <CalculatorShell
      calculator={calculator}
      onBack={onBack}
      isFavorite={isFavorite}
      onToggleFavorite={onToggleFavorite}
      onExportPDF={exportData ? handleExportPDF : undefined}
      onShareWhatsApp={exportData ? handleShareWhatsApp : undefined}
    >
      {renderCalculator()}
    </CalculatorShell>
  );
};
